"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, CircleCheck as CheckCircle2, FlaskConical, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface StepItem {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  metric: string;
}
const synthesisSteps: StepItem[] = [{
  number: "01",
  title: "Solid-phase assembly",
  subtitle: "Fmoc chemical synthesis",
  description: "Stepwise chain elongation on polystyrene resin cores with automated optical coupling verification.",
  metric: "Coupling efficiency: >99.4%"
}, {
  number: "02",
  title: "Preparative RP-HPLC",
  subtitle: "Reverse-phase purification",
  description: "Acetonitrile gradient fraction collector separating truncations and diastereomeric impurities.",
  metric: "Purity benchmark: ≥98.0%"
}, {
  number: "03",
  title: "Lyophilization & assay",
  subtitle: "Vacuum drying & LC-MS",
  description: "Multi-day deep vacuum dehydration yielding sterile cakes, followed by ESI mass spectrometry validation.",
  metric: "Residual moisture: <2.5%"
}];
export default function ResearchOverview() {
  const prefersReducedMotion = useReducedMotion();
  const animationProps = prefersReducedMotion ? {
    initial: {
      opacity: 1
    },
    whileInView: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="research-overview" data-nav="light" className="bg-background text-foreground py-14 md:py-20 border-b border-border">
      <div className="max-w-[1088px] mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: Documentary Laboratory Visual */}
          <motion.div {...animationProps} className="lg:col-span-5 flex flex-col gap-4">
            <div className="relative rounded-md overflow-hidden border border-border bg-muted aspect-4/5 shadow-xs">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/analytical_chemist_chromatography_lab.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/analytical_chemist_chromatography_lab.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/analytical_chemist_chromatography_lab.webp-wvc-sizes" alt="Analytical chemist calibrating preparative chromatography system in a cleanroom laboratory" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-secondary/10 pointer-events-none" />

              {/* Monospace Assay Tag */}
              <div className="absolute bottom-3 left-3 right-3 bg-background/95 backdrop-blur-xs border border-border rounded-sm p-3 text-xs flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary inline-block" />
                  <span className="font-mono text-muted-foreground uppercase text-[11px] tracking-wider">
                    Assay: RP-HPLC Calibration
                  </span>
                </div>
                <span className="font-mono text-foreground font-semibold text-[11px]">
                  Batch Lot 24-B98
                </span>
              </div>
            </div>

            {/* Empirical Summary Card */}
            <div className="rounded-md border border-border bg-card p-4 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2.5">
                <FlaskConical className="w-4 h-4 text-primary shrink-0" />
                <span className="text-muted-foreground">Certified Synthesis Standard</span>
              </div>
              <span className="font-mono text-foreground font-medium">ISO 9001 / cGMP</span>
            </div>
          </motion.div>

          {/* Right Column: Technical Specification & Process Steps */}
          <motion.div {...animationProps} className="lg:col-span-7 flex flex-col justify-center">
            {/* Eyebrow Kicker */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-4 h-px bg-primary" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-primary font-bold">
                Synthesis architecture
              </span>
            </div>

            {/* Heading */}
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground font-default mb-4">
              Solid-phase peptide synthesis and lyophilization protocols
            </h2>

            {/* Technical Narrative */}
            <div className="space-y-3 text-sm text-foreground/85 leading-relaxed mb-8">
              <p>
                Our laboratory utilizes orthogonal Fmoc/tBu solid-phase methodologies to build target
                amino acid sequences on insoluble matrix supports. Acid-labile linkers allow
                controlled cleavage via concentrated trifluoroacetic acid (TFA) cocktails with tailored
                scavengers, preventing re-attachment of reactive side-chain protecting groups.
              </p>
              <p>
                Following ether precipitation and primary centrifugal isolation, crude peptides are
                channeled through dual-wavelength preparative reverse-phase columns (C18 silica matrix).
                Subsequent deep vacuum lyophilization removes volatile counter-ions, yielding stable,
                moisture-controlled reagents ready for reconstitution.
              </p>
            </div>

            {/* Process Architecture Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-8">
              {synthesisSteps.map((step, i) => <div key={step.number} data-index={i} className="rounded-md border border-border bg-card p-4 flex flex-col justify-between transition-colors hover:border-primary/50">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs font-semibold text-primary">
                        {step.number}
                      </span>
                      <Activity className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-1 leading-snug">
                      {step.title}
                    </h3>
                    <p className="text-[11px] font-mono text-muted-foreground mb-2">
                      {step.subtitle}
                    </p>
                    <p className="text-xs text-foreground/75 leading-normal mb-3">
                      {step.description}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-border mt-auto">
                    <span className="font-mono text-[10px] text-muted-foreground block">
                      {step.metric}
                    </span>
                  </div>
                </div>)}
            </div>

            {/* Quality Action Strip */}
            <div className="rounded-md border border-border bg-muted/60 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-background border border-border flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-foreground">
                    Batch-specific analytical data
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Each shipment includes individual HPLC chromatograms and mass spectral reports.
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                <Button asChild variant="outline" size="sm" className="w-full sm:w-auto text-xs h-8">
                  <Link to="/ShopPage">
                    Browse catalog
                  </Link>
                </Button>
                <Button asChild size="sm" className="w-full sm:w-auto text-xs h-8">
                  <Link to="/ContactPage">
                    Custom synthesis
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}